// features/reports/components/ForecastReport.tsx
// ============================================================================
// PRONÓSTICO Y PRESUPUESTO — el reporte mensual de gestión (para Mabel, la socia). 26-sep-2026.
//
// Plan: forobeta-ops/letsmove/PLAN-PRESUPUESTO-VS-REAL-20260926.md. Antes este tab solo enseñaba
// «esperado vs cobrado» del mes en curso. Ahora: selector de mes, las 8 filas de la hoja de Mabel
// (Presupuesto vs Real), mensualidades por origen (la respuesta a «están mezcladas mensualidades de
// otros meses»), nómina calculada vs registrada, gastos, resultado y pronóstico del mes siguiente.
// Todo lo arma el servidor (/finances/presupuesto): pantalla, Excel y PDF cuadran por construcción.
// Tres cifras con nombre propio, nunca mezcladas: Presupuesto · Real devengado · Caja.
// ============================================================================
import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, FileSpreadsheet, FileText, PiggyBank, TrendingUp, Wallet, Users, ChevronDown, AlertTriangle } from 'lucide-react';
import { getPresupuesto, urlExportPresupuesto, Presupuesto, moneyEntero as money } from '../../finances/api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import CountUp from '../../../components/CountUp';
import PorApartado from './PorApartado';
import Conciliacion from './Conciliacion';
import EstadoResultados from './EstadoResultados';
// «Costo docente» (B4) salió de las sub-pestañas el 20-oct-2026 (Mabel, #1484): su cálculo sigue en CostoDocente.tsx /
// GET /presupuesto/costo-docente y lo reutiliza «Punto de equilibrio y rentabilidad por clase» (pestaña propia en Reportes).
import PresupuestoGastosReporte from './PresupuestoGastosReporte';
import CierreEstimado from './CierreEstimado';
import MiEscenario from './MiEscenario';
import PresupuestoPeriodo from './PresupuestoPeriodo';
import { SelectorVista, NavPeriodo, periodoDe, VistaPeriodo } from './PeriodoSelector';
import { urlExportPresupuestoPeriodo } from '../api';

const mesLocal = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Tijuana' }).slice(0, 7);
function sumaMeses(ym: string, n: number) { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
const signo = (n: number) => (n >= 0 ? '+' : '−') + money(Math.abs(n));
const pctTxt = (p: number | null) => (p == null ? '' : `${p >= 0 ? '+' : '−'}${Math.abs(p).toFixed(1)} %`);
const colorVar = (v: number | null) => (v == null ? 'text-content-3' : v >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400');
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-40';
const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';

export default function ForecastReport() {
  const [mes, setMes] = useState<string>(mesLocal());
  const [d, setD] = useState<Presupuesto | null>(() => readCache<Presupuesto>(`rep-presupuesto-${mesLocal()}`) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verOrigen, setVerOrigen] = useState(true);
  const [verNomina, setVerNomina] = useState(false);
  const [verGastos, setVerGastos] = useState(false);
  // B0 (14-oct-2026): «Conciliación» vive aquí como sub-pestaña (misma fuente que el presupuesto).
  // B1 (15-oct-2026): «Estado de resultados» (operativo y neto, margen, acumulado), misma fuente que Por apartado.
  // B3 (16-oct-2026): «Cierre estimado» (cierre del mes + mes siguiente, 2 perillas → 3 escenarios), su propia llamada.
  const [vista, setVista] = useState<'resumen' | 'cierre' | 'resultados' | 'conciliacion'>('resumen');
  // Mes | Trimestre | Semestre (Mabel 23-oct-2026). Trimestre/semestre = suma de los meses (PresupuestoPeriodo).
  const [vistaP, setVistaP] = useState<VistaPeriodo>('mes');
  // Ventana móvil (Mabel 24-oct-2026, «¿puedo recorrer los trimestres, por ejemplo feb–abril?»): `mes` = 1.er mes de la ventana.
  const [movilP, setMovilP] = useState(false);
  const cambiarVistaP = (v: VistaPeriodo) => { setVistaP(v); if (v === 'mes') setMovilP(false); };

  // Pinta al instante lo último conocido del mes (caché local) y refresca por detrás.
  useEffect(() => {
    if (vistaP !== 'mes') return;
    let vivo = true;
    const cached = readCache<Presupuesto>(`rep-presupuesto-${mes}`);
    setD(cached ?? null); setError(null); setCargando(!cached);
    getPresupuesto(mes)
      .then((r) => { if (!vivo) return; setD(r); writeCache(`rep-presupuesto-${mes}`, r); })
      .catch((e: any) => { if (vivo) setError(e?.message || 'No se pudo cargar el presupuesto'); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [mes, vistaP]);

  // 23-oct-2026 (Mabel): un mes futuro sale en modo «Pronóstico» (tentativo); se puede avanzar hasta 12 meses.
  const esFuturo = mes >= sumaMeses(mesLocal(), 12);
  const pr = d?.pronostico ?? null;
  const baseCorta = pr ? pr.etiqueta.replace(/^Pronóstico /, '') : '';

  // ── Trimestre / semestre: resumen sumado o estado de resultados del periodo. Cierre y Conciliación son de un mes.
  if (vistaP !== 'mes') {
    const p = periodoDe(vistaP, mes, movilP);
    const hastaReal = p.hasta < mesLocal() ? p.hasta : mesLocal();
    const vistaPer = vista === 'resultados' ? 'resultados' : 'resumen';
    return (
      <div className="space-y-4">
        <SelectorVista vista={vistaP} onChange={cambiarVistaP} />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <NavPeriodo tipo={vistaP} mes={mes} movil={movilP} onChange={(ym, m) => { setMes(ym); setMovilP(m); }} maxAdelante={vistaP === 'trimestre' ? 4 : 2} />
          <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-line bg-surface p-0.5 text-sm" role="tablist" aria-label="Vista">
            {([['resumen', `Resumen del ${vistaP}`], ['resultados', 'Estado de resultados']] as const).map(([k, label]) => (
              <button key={k} role="tab" aria-selected={vistaPer === k} onClick={() => setVista(k)}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 transition ${vistaPer === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>{label}</button>
            ))}
          </div>
          {vistaPer === 'resumen' && (
            <a href={urlExportPresupuestoPeriodo(vistaP, mes, movilP)} download title={`Descargar Excel del ${vistaP}`} className={`${navBtn} flex items-center gap-1.5 text-sm`}>
              <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Excel</span>
            </a>
          )}
        </div>
        {vistaPer === 'resumen' && <PresupuestoPeriodo tipo={vistaP} mes={mes} movil={movilP} />}
        {vistaPer === 'resultados' && (p.desde > mesLocal()
          ? <p className="rounded-xl border border-line bg-surface p-4 text-sm text-content-3">Este {vistaP} aún no empieza: el estado de resultados sale con datos reales. Mira su pronóstico en «Resumen del {vistaP}».</p>
          : <EstadoResultados mes={hastaReal} desde={p.desde} />)}
        <p className="text-xs text-content-3">Cierre estimado y Conciliación se ven por mes (botón «Mes»).</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SelectorVista vista={vistaP} onChange={cambiarVistaP} />
      {/* Selector de mes + exportar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setMes(sumaMeses(mes, -1))} title="Mes anterior" className={navBtn}><ChevronLeft size={16} /></button>
          <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)}
            className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue" />
          <button onClick={() => setMes(sumaMeses(mes, 1))} disabled={esFuturo} title="Mes siguiente" className={navBtn}><ChevronRight size={16} /></button>
          {mes !== mesLocal() && <button onClick={() => setMes(mesLocal())} className={`${navBtn} text-xs`}>Hoy</button>}
        </div>
        <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-line bg-surface p-0.5 text-sm" role="tablist" aria-label="Vista">
          {([['resumen', 'Resumen del mes'], ['cierre', 'Cierre estimado'], ['resultados', 'Estado de resultados'], ['conciliacion', 'Conciliación']] as const).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={vista === k} onClick={() => setVista(k)}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 transition ${vista === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>{label}</button>
          ))}
        </div>
        {d && vista === 'resumen' && (
          <div className="flex items-center gap-2">
            <a href={urlExportPresupuesto(mes, 'xlsx')} download title="Descargar Excel (organizado como el libro de presupuesto)" className={`${navBtn} flex items-center gap-1.5 text-sm`}>
              <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Excel</span>
            </a>
            <a href={urlExportPresupuesto(mes, 'pdf')} target="_blank" rel="noopener" title="Abrir PDF con el logo" className={`${navBtn} flex items-center gap-1.5 text-sm`}>
              <FileText size={16} /> <span className="hidden sm:inline">PDF</span>
            </a>
          </div>
        )}
      </div>

      {vista === 'cierre' && <CierreEstimado mes={mes} />}
      {vista === 'conciliacion' && <Conciliacion mes={mes} />}
      {vista === 'resultados' && <EstadoResultados mes={mes} />}
      {vista === 'resumen' && error && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">{error}</p>}
      {vista === 'resumen' && !d && !error && <Skeleton className="h-96 w-full rounded-2xl" />}
      {vista === 'resumen' && d && (
        <div className={cargando ? 'opacity-60 transition' : 'transition'}>
          <p className="mb-3 text-sm text-content-3">
            <b className="text-content-2">{d.titulo}</b>{mes === mesLocal() ? ' (mes en curso)' : ''}
            {pr && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Pronóstico · tentativo</span>}
            {d.mesAnterior.real > 0 && d.mesAnterior.pct != null && (
              <span className={`ml-2 ${colorVar(d.mesAnterior.diff)}`}>vs {d.mesAnterior.titulo.toLowerCase()} (devengado): {signo(d.mesAnterior.diff)} ({pctTxt(d.mesAnterior.pct)})</span>
            )}
          </p>

          {/* KPI: las tres cifras con nombre propio + variación */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className={card}>
              <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><Users size={13} /> {pr ? 'Ingresos pronosticados' : 'Presupuesto'}</div>
              <div className="mt-1 font-display text-xl font-bold text-content"><CountUp value={d.presupuesto.total} format={money} /></div>
              <div className="mt-0.5 text-xs text-content-3">{pr ? `activos × plan + ${baseCorta}` : d.presupuesto.conObjetivo ? d.presupuesto.etiqueta : `${d.presupuesto.alumnas} alumnas activas × su plan`}</div>
            </div>
            <div className={card}>
              <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><TrendingUp size={13} className="text-brand-blue" /> Real devengado</div>
              <div className="mt-1 font-display text-xl font-bold text-content"><CountUp value={d.real.total} format={money} /></div>
              <div className="mt-0.5 text-xs text-content-3">lo que corresponde al mes · {d.real.n} cobros</div>
            </div>
            {pr ? (
              <div className={card}>
                <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><PiggyBank size={13} /> Resultado tentativo</div>
                <div className={`mt-1 font-display text-xl font-bold ${colorVar(pr.resultado)}`}><CountUp value={pr.resultado} format={money} /></div>
                <div className="mt-0.5 text-xs text-content-3">pronóstico, no es real</div>
              </div>
            ) : (
            <div className={card}>
              <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><PiggyBank size={13} /> {d.presupuesto.conObjetivo ? 'Variación vs objetivo' : 'Variación mensualidades'}</div>
              <div className={`mt-1 font-display text-xl font-bold ${colorVar(d.variacion.abs)}`}>{signo(d.variacion.abs)}</div>
              <div className="mt-0.5 text-xs text-content-3">{d.variacion.pct == null ? 'sin presupuesto' : `${pctTxt(d.variacion.pct)} vs presupuesto`}</div>
            </div>
            )}
            <div className={card}>
              <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><Wallet size={13} /> Cobrado en caja</div>
              <div className="mt-1 font-display text-xl font-bold text-content"><CountUp value={d.caja.total} format={money} /></div>
              <div className="mt-0.5 text-xs text-content-3">{d.caja.adelantosDados.total > 0 ? `incluye ${money(d.caja.adelantosDados.total)} de adelantos` : `${d.caja.n} cobros del mes`}</div>
            </div>
          </div>

          {/* Mes FUTURO (Mabel 23-oct-2026): pronóstico tentativo = ingresos pronosticados − nómina − gastos presupuestados. */}
          {pr && (
            <div className={`${card} mt-4 border-amber-300 dark:border-amber-800`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-display text-base font-bold text-content">Pronóstico de {pr.titulo.toLowerCase()} <span className="text-sm font-normal text-amber-700 dark:text-amber-400">(tentativo, no es real)</span></h3>
                <span className="text-xs text-content-3">{pr.nota}</span>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="mb-1 text-xs uppercase tracking-wide text-content-3">Ingresos pronosticados</div>
                {pr.ingresos.porConcepto.map((x) => (
                  <div key={x.k} className="flex items-center justify-between border-b border-line/60 py-1.5">
                    <span className="text-content-2">{x.categoria} · {x.concepto} <span className="text-xs text-content-3">{x.origen}</span>
                      {/* Festival del mes (Eventos › Finanzas del evento, 26-oct-2026) */}
                      {!!x.festival && <span className="ml-1.5 rounded bg-accent/15 px-1.5 py-0.5 text-[11px] font-semibold text-accent-ink">incluye festival {money(x.festival)}</span>}</span>
                    <span className="tabular-nums text-content">{money(x.monto)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-1.5 font-semibold"><span className="text-content">Total ingresos pronosticados</span><span className="tabular-nums text-content">{money(pr.ingresos.total)}</span></div>
                <div className="mb-1 mt-3 text-xs uppercase tracking-wide text-content-3">Egresos pronosticados</div>
                <div className="flex items-center justify-between border-b border-line/60 py-1.5"><span className="text-content-2">Nómina: horario × tarifas y sueldo fijo</span><span className="tabular-nums text-content">{money(pr.nomina.horario)}</span></div>
                {pr.nomina.comision > 0 && (
                  <div className="flex items-center justify-between border-b border-line/60 py-1.5"><span className="text-content-2">Nómina: comisión estimada <span className="text-xs text-content-3">{pr.nomina.comisionEstimada ? `con las cuotas de ${baseCorta.replace(/^con lo real de /, '')}` : ''}</span></span><span className="tabular-nums text-content">{money(pr.nomina.comision)}</span></div>
                )}
                {pr.gastos.porApartado.map((a) => (
                  <div key={a.section} className="flex items-center justify-between border-b border-line/60 py-1.5"><span className="text-content-2">Gastos presupuestados · {a.apartado}{a.section === 'nomina' ? <span className="text-xs text-content-3"> (sin «Nómina» ni comisiones)</span> : null}</span><span className="tabular-nums text-content">{money(a.total)}</span></div>
                ))}
                {pr.gastos.vacio && <p className="text-xs text-amber-700 dark:text-amber-400">No hay presupuesto de gastos capturado para este mes (Finanzas › Presupuesto).</p>}
              </div>
              <div className={`mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-3 ${pr.resultado >= 0 ? 'bg-on-accent text-white' : 'bg-red-700 text-white'}`}>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide opacity-90">Resultado tentativo (pronóstico)</div>
                  <div className="text-xs opacity-70">ingresos pronosticados {money(pr.ingresos.total)} − nómina {money(pr.nomina.total)} − gastos presupuestados {money(pr.gastos.total)}</div>
                </div>
                <div className="font-display text-2xl font-bold"><CountUp value={pr.resultado} format={money} /></div>
              </div>
              <div className="mt-2 space-y-1 text-xs text-content-3">
                {pr.notas.map((n) => <p key={n}>{n}</p>)}
                {pr.gastos.excluidos.length > 0 && <p>Fuera de gastos para no contarlos dos veces: {pr.gastos.excluidos.map((x) => `${x.concepto} ${money(x.monto)} (${x.motivo})`).join(' · ')}.</p>}
              </div>
            </div>
          )}

          {/* «Mi escenario» (Mabel 23-oct-2026 12:35): copia editable del pronóstico, aparte; solo meses futuros. */}
          {pr && (
            <MiEscenario mes={mes} pr={pr} escenario={d.escenario}
              onGuardado={(ov) => setD((prev) => {
                if (!prev) return prev;
                const n = { ...prev, escenario: { mes, updated_at: prev.escenario?.updated_at ?? null, updated_by: prev.escenario?.updated_by ?? null, ov } };
                writeCache(`rep-presupuesto-${mes}`, n);
                return n;
              })} />
          )}

          {/* Catálogo de Mabel (7-oct-2026): Apartado › Categoría › Concepto. */}
          {d.estructura && <div className="mt-4"><PorApartado e={d.estructura} /></div>}

          {/* Tabla de Mabel: Presupuesto vs Real */}
          <div className={`${card} mt-4 overflow-x-auto p-0`}>
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h3 className="font-display text-base font-bold text-content">{pr ? 'Ingresos · Pronóstico vs ya cobrado' : 'Ingresos · Presupuesto vs Real'}</h3>
              <span className="text-xs text-content-3">{d.real.n} cobros</span>
            </div>
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
                  <th className="px-4 py-2 font-medium">Categoría</th>
                  <th className="px-2 py-2 font-medium">Concepto</th>
                  <th className="px-2 py-2 text-right font-medium">{pr ? 'Pronóstico' : 'Presupuesto'}</th>
                  <th className="px-2 py-2 text-right font-medium">Real</th>
                  <th className="px-2 py-2 text-right font-medium">Variación</th>
                  <th className="px-2 py-2 text-right font-medium">Cobros</th>
                  <th className="px-4 py-2 text-right font-medium" title="Promedio de los 3 meses anteriores">Ref. 3 meses</th>
                </tr>
              </thead>
              <tbody>
                {d.filas.map((f, i) => (
                  <tr key={f.k} className={`border-b border-line/60 ${i % 2 ? 'bg-surface-2/40' : ''}`}>
                    <td className="px-4 py-2 text-content-2">{f.categoria}</td>
                    <td className="px-2 py-2 text-content">{f.concepto}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-content-3" title={f.origen}>{f.presupuesto == null ? '—' : money(f.presupuesto)}{f.origen === 'objetivo capturado' && <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" title="Objetivo capturado en Finanzas › Presupuesto › Ingresos">obj</span>}</td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-content">{money(f.real)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${colorVar(f.variacion)}`}>{f.variacion == null ? '—' : <>{signo(f.variacion)} <span className="text-xs">{pctTxt(f.pct)}</span></>}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-content-3">{f.n || ''}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs text-content-3">{f.referencia == null ? (f.k === 'mensualidad' ? 'activos × plan' : '') : money(f.referencia)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surface-2/70 font-semibold">
                  <td className="px-4 py-2 text-content" colSpan={2}>Total ingresos</td>
                  <td className="px-2 py-2 text-right tabular-nums text-content-2">{money(d.presupuesto.total)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-content">{money(d.real.total)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${colorVar(d.variacion.abs)}`}>{signo(d.variacion.abs)} <span className="text-xs">{pctTxt(d.variacion.pct)}</span></td>
                  <td className="px-2 py-2 text-right tabular-nums text-content-3">{d.real.n}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
            {pr ? (
              <p className="px-4 pb-4 pt-2 text-xs text-content-3">Pronóstico (tentativo): mensualidades = alumnos activos × su plan; los demás conceptos {baseCorta} ({pr.nota.replace(/^[^(]*\(/, '').replace(/\)\.?$/, '')}). Real = lo ya cobrado por adelantado para este mes.</p>
            ) : (
            <p className="px-4 pb-4 pt-2 text-xs text-content-3">
              Presupuesto: solo mensualidades (alumnos activos × su plan). Los demás conceptos no tienen presupuesto; «Ref. 3 meses» es el promedio de los tres meses anteriores, por si quieren fijar uno.
              {d.presupuesto.nota ? ` ${d.presupuesto.nota}` : ''}
            </p>
            )}
          </div>

          {/* Mensualidades por origen */}
          <div className={`${card} mt-4`}>
            <button onClick={() => setVerOrigen(!verOrigen)} className="flex w-full items-center justify-between gap-3 text-left">
              <h3 className="font-display text-base font-bold text-content">Mensualidades por origen</h3>
              <span className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-right text-xs text-content-3">lo que corresponde a este mes <ChevronDown size={16} className={`shrink-0 transition ${verOrigen ? 'rotate-180' : ''}`} /></span>
            </button>
            {verOrigen && (
              <div className="mt-3 space-y-1 text-sm">
                {([
                  ['Cobradas este mes para este mes', d.mensualidadesOrigen.este_mes],
                  ['Cobradas antes (adelantos que ya habías recibido)', d.mensualidadesOrigen.adelantos_recibidos],
                  ['Cobradas después (atrasadas)', d.mensualidadesOrigen.atrasadas],
                  ['Paquetes de varios meses (parte proporcional)', d.mensualidadesOrigen.paquetes],
                ] as const).map(([label, v]) => (
                  <div key={label} className="flex items-center justify-between border-b border-line/60 py-1.5">
                    <span className="text-content-2">{label}</span>
                    <span className="tabular-nums"><span className="mr-3 text-xs text-content-3">{v.n || ''}</span>{money(v.total)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-1.5 font-semibold">
                  <span className="text-content">Total mensualidades del mes</span>
                  <span className="tabular-nums text-content">{money(d.filas[0]?.real ?? 0)}</span>
                </div>
                {d.caja.adelantosDados.total > 0 && (
                  <p className="pt-1 text-xs text-content-3">Aparte, este mes se cobraron <b className="text-content-2">{money(d.caja.adelantosDados.total)}</b> por adelantado para meses siguientes ({d.caja.adelantosDados.n} pagos): están en la caja del mes, pero no en este real.</p>
                )}
              </div>
            )}
          </div>

          {/* Nómina */}
          <div className={`${card} mt-4`}>
            <button onClick={() => setVerNomina(!verNomina)} className="flex w-full items-center justify-between gap-3 text-left">
              <h3 className="font-display text-base font-bold text-content">Nómina del mes</h3>
              <span className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-right text-xs text-content-3">
                calculada <b className="text-content">{money(d.nomina.calculada)}</b>{d.nomina.registrada > 0 && <> · registrada <b className="text-content">{money(d.nomina.registrada)}</b></>}
                <ChevronDown size={16} className={`shrink-0 transition ${verNomina ? 'rotate-180' : ''}`} />
              </span>
            </button>
            {verNomina && (
              <div className="mt-3 overflow-x-auto">
                {d.nomina.porProfe.length ? (
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
                        <th className="py-2 pr-2 font-medium">Profesor</th><th className="py-2 pr-2 font-medium">Modo</th>
                        <th className="py-2 pr-2 text-right font-medium">Horas</th><th className="py-2 pr-2 text-right font-medium">Calculado</th>
                        <th className="py-2 pr-2 text-right font-medium">Registrado</th><th className="py-2 text-right font-medium">Sin registrar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.nomina.porProfe.map((p) => (
                        <tr key={p.user_id} className="border-b border-line/60">
                          <td className="py-1.5 pr-2 text-content">{p.nombre}</td>
                          <td className={`py-1.5 pr-2 text-xs ${p.sin_tarifa ? 'text-red-600' : 'text-content-3'}`}>{p.modo}{p.sin_tarifa ? ' · sin tarifa' : ''}{p.base != null ? ` · base ${money(p.base)}` : ''}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{p.horas ?? ''}</td>
                          <td className="py-1.5 pr-2 text-right font-semibold tabular-nums text-content">{money(p.calculado)}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{money(p.registrado)}</td>
                          <td className={`py-1.5 text-right tabular-nums ${p.diferencia > 0 ? 'text-red-600 dark:text-red-400' : 'text-content-3'}`}>{p.diferencia === 0 ? '—' : money(p.diferencia)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="text-sm text-content-3">Sin clases con profesor en el horario este mes.</p>}
                {d.nomina.nota && <p className="mt-2 text-xs text-content-3">{d.nomina.nota}</p>}
                <p className="mt-1 text-xs text-content-3">Calculado = sesiones del horario × tarifa (sin las «no se dio»; suplencias a quien las dio) y comisiones. Registrado = pagado desde Finanzas › Nómina y comisiones entregadas.</p>
              </div>
            )}
          </div>

          {/* Gastos + resultado */}
          <div className={`${card} mt-4`}>
            <button onClick={() => setVerGastos(!verGastos)} className="flex w-full items-center justify-between gap-3 text-left">
              <h3 className="font-display text-base font-bold text-content">Gastos del mes</h3>
              <span className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-right text-xs text-content-3">
                {d.gastos.porCategoria.length ? <>capturados <b className="text-content">{money(d.gastos.reales)}</b></> : <span className="flex items-center gap-1 text-amber-600"><AlertTriangle size={13} /> sin gastos capturados</span>}
                {d.gastos.programados > 0 && <> · programados <b className="text-content">{money(d.gastos.programados)}</b></>}
                <ChevronDown size={16} className={`shrink-0 transition ${verGastos ? 'rotate-180' : ''}`} />
              </span>
            </button>
            {/* B2 (15-oct-2026): el recuadro «Programados» (salía vacío) se volvió «Presupuesto de gastos», debajo, a todo
                el ancho. Aquí queda la lista de gastos por categoría en una sola columna. */}
            {verGastos && (
              <div className="mt-3 text-sm">
                {d.gastos.porCategoria.length ? d.gastos.porCategoria.map((c) => (
                  <div key={c.categoria} className="flex items-center justify-between border-b border-line/60 py-1.5">
                    <span className="text-content-2">{c.categoria}{c.nomina ? ' (nómina registrada)' : ''}</span>
                    <span className="tabular-nums"><span className="mr-3 text-xs text-content-3">{c.n}</span>{money(c.total)}</span>
                  </div>
                )) : <p className="text-xs text-amber-700 dark:text-amber-400">{d.gastos.nota}</p>}
                {d.gastos.recurrentes.length > 0 && (
                  <>
                    <div className="mb-1 mt-3 text-xs uppercase tracking-wide text-content-3">Programados</div>
                    {d.gastos.recurrentes.map((r) => (
                      <div key={r.nombre + r.dia} className="flex items-center justify-between border-b border-line/60 py-1.5">
                        <span className="text-content-2">{r.nombre} <span className="text-xs text-content-3">día {r.dia}</span></span>
                        <span className="tabular-nums">{money(r.importe)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
            <div className={`mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-3 ${d.resumen.resultado >= 0 ? 'bg-on-accent text-white' : 'bg-red-700 text-white'}`}>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide opacity-90">{pr ? 'Resultado tentativo (pronóstico)' : `Resultado del mes${d.resumen.incompleto ? ' (incompleto)' : ''}`}</div>
                <div className="text-xs opacity-70">{pr
                  ? `ingresos pronosticados ${money(pr.ingresos.total)} − nómina ${money(pr.nomina.total)} − gastos presupuestados ${money(pr.gastos.total)}`
                  : `ingresos devengados ${money(d.resumen.ingresosReal)} − nómina calculada ${money(d.resumen.nominaCalculada)} − gastos ${money(d.resumen.gastosReales)}`}</div>
              </div>
              <div className="font-display text-2xl font-bold"><CountUp value={d.resumen.resultado} format={money} /></div>
            </div>
            {d.resumen.incompleto && <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">Faltan los gastos del mes: captúrenlos en Finanzas › Gastos y este resultado se completa solo.</p>}
          </div>

          {/* B2 (15-oct-2026, Mabel): presupuesto de gastos del mes vs real, por categoría › concepto. Su propia
              llamada (/presupuesto/gastos): si falla, el resto del reporte no se entera. */}
          <PresupuestoGastosReporte mes={mes} />

          {/* Pronóstico del mes siguiente */}
          <div className={`${card} mt-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-content">Pronóstico de {d.siguiente.titulo.toLowerCase()}</h3>
              <span className="text-xs text-content-3">{d.siguiente.alumnas} alumnas activas</span>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex items-center justify-between border-b border-line/60 py-1.5"><span className="text-content-2">Presupuesto de mensualidades (activos × plan, precios de hoy)</span><span className="font-semibold tabular-nums text-content">{money(d.siguiente.presupuesto)}</span></div>
              <div className="flex items-center justify-between border-b border-line/60 py-1.5"><span className="text-content-2">Ya cobrado por adelantado ({d.siguiente.pagos} pagos)</span><span className="tabular-nums text-content-2">{money(d.siguiente.yaCobrado)}</span></div>
              <div className="flex items-center justify-between py-1.5"><span className="text-content-2">Pendiente por cobrar si todos conservan su plan</span><span className="font-semibold tabular-nums text-brand-blue">{money(Math.max(0, d.siguiente.presupuesto - d.siguiente.yaCobrado))}</span></div>
            </div>
            <p className="mt-2 text-xs text-content-3">{d.siguiente.nota}</p>
          </div>

          <p className="mt-4 text-xs text-content-3">
            <b className="text-content-2">Presupuesto</b> = lo que debería entrar si los alumnos activos pagan su plan. <b className="text-content-2">Real devengado</b> = lo que corresponde al mes, se haya cobrado antes, durante o después. <b className="text-content-2">Caja</b> = lo que entró en el mes, como en Finanzas › Ingresos. Generado el {d.generado}.
          </p>
        </div>
      )}
    </div>
  );
}
